"use client";

import { Button } from "@workspace/ui/components/button";
import { ArrowLeftIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import type { ComponentProps } from "react";

/**
 * "Go back", meaning it: the entry behind this one when the tab has one, and
 * the homepage when it does not. Someone who opened a dead link in a fresh
 * tab has nothing behind them, and a back button that quietly does nothing is
 * the worst thing to hand a person who is already lost.
 *
 * The branch is taken on click rather than on mount, so it reads the history
 * as it stands when pressed and the label never has to change out from under
 * a hydration.
 */
export function BackButton({
  fallbackHref = "/",
  children = "Go back",
  ...props
}: ComponentProps<typeof Button> & { fallbackHref?: string }) {
  const router = useRouter();

  return (
    <Button
      {...props}
      onClick={() => {
        if (window.history.length > 1) {
          router.back();
          return;
        }
        router.push(fallbackHref);
      }}
    >
      <ArrowLeftIcon className="size-4" />
      {children}
    </Button>
  );
}
